
//@ts-check 
import { LayoutManager } from './LayoutManager';
import { PropertyManager } from "./components/PropertyPanel/PropertyManager";
import { ToolboxManager } from './ToolboxManager';
import { DragDropManager } from './DragDropManager';
import { ViewportManager } from './ViewportManager';
import { changeType, StateManager } from './store/StateManager';
import { ILayoutBuilderOptions, IScreenSize, ScreenMeasuringUnit } from './types';
import { LayoutConfig } from "./sampleConfig";
import { CSS_CLASSES } from './constants/CSSClasses';
import { ScaleRuler } from './components/controls/ruler/Ruler';
import './components/controls/ruler/Ruler.css';
import {DomManager} from './managers/DOMManager'
import { Slider } from './components/controls/slider';
import { EventCallback, EventEmitter, EVENTS } from './store/EventEmitter';
class Resizer {
  private onResize: () => void;
 
  constructor(onResize: () => void) {
    this.onResize = onResize;
    window.addEventListener("resize", this.onResize);
  }
 
  destroy(): void {
    window.removeEventListener("resize", this.onResize);
  }
}
export class LayoutBuilder {
  private layoutManager: LayoutManager;
  private propertyManager: PropertyManager;
  private toolboxManager: ToolboxManager;
  private host: HTMLElement;
  private eventEmitter: EventEmitter<any>;
  private viewportManager: ViewportManager;
  private dragdropManager: DragDropManager;
  private stateManager: StateManager;
  private options: ILayoutBuilderOptions;
  private ruler:ScaleRuler|null = null;  
  private rcExpIncValue:number = 0
  private rcExpIncreamentor:Slider;
  private viewportZoomValue:number = 100;  
  private viewportZoomControl:Slider;
  private screenSizeControl:HTMLSelectElement|null = null;
 
  constructor(host: HTMLDivElement, options: ILayoutBuilderOptions) {
    this.options = options;
    if(!this.options.screenSizes || this.options.screenSizes.length==0){
      this.options.screenSizes = [
        {label:"Mobile (15cm x 7cm)",width:15,height:7, unit:ScreenMeasuringUnit.cm},
        {label:"Desktop (30cm x 15cm)",width:30,height:15, unit:ScreenMeasuringUnit.cm},
        {label:"Tablet (20cm x 10cm)",width:20,height:10, unit:ScreenMeasuringUnit.cm},
        {label:"Custom (1424px x 500px)",width:1424,height:500, unit:3}
      ]
    }
    this.host = host;
    // Create required DOM elements dynamically
    this.createStructure();
    // Initialize components
    this.stateManager = StateManager.getInstance();
    this.layoutManager = new LayoutManager(host.querySelector('#viewport-container')!, host.querySelector('#sidebar .layer-panel')!);
    this.propertyManager = new PropertyManager(host.querySelector('#layer-property-container .property-panel')!,this.options.mediaDeliveryService);
    this.toolboxManager = new ToolboxManager(host.querySelector('#toolbox .toolbox-panel')!);
    this.viewportManager = new ViewportManager(host.querySelector('#viewport-container')!);
    this.dragdropManager = new DragDropManager(host);
    this.ruler = new ScaleRuler("viewport",ScreenMeasuringUnit.cm)
    const resizer = new Resizer((): void => {
      this.refresh();
    });
 
    this.rcExpIncreamentor = new Slider("gridLineDensitySlider","Density", -3, 2, this.rcExpIncValue,() => this.changeGridDensity(),(val)=>{
      return `${1/Math.pow(2,val)}mm`;
    });
    this.viewportZoomControl = new Slider("gridLineZoomSlider","Zoom", 30, 500, this.viewportZoomValue,() => this.changeZoom(),(val)=>{
      return `${val}%`;
    });
    let lastZoomTime:number = 0;
    let avgDeltaY = 0
    const viewPort = document.getElementById("viewport") as HTMLDivElement;
    viewPort.addEventListener('scroll',(event)=>{
      if(this.ruler)
        this.ruler.scroll(viewPort.scrollLeft, viewPort.scrollTop);
    });
    viewPort.addEventListener('wheel', (event) => {
      if (event.ctrlKey) {
        //console.log('deltaY:', event.deltaY); // Scroll up/down
        //console.log('deltaX:', event.deltaX); // Scroll left/right
        //console.log('deltaMode:', event.deltaMode); // Unit of measurement
        if((lastZoomTime == 5 && event.deltaY < 0) || event.deltaY < -100){
          console.log('lastZoomTime:', lastZoomTime); // Unit of measurement
          this.viewportZoomControl.changeValue(this.viewportZoomControl.value + 10)
        }
        else if((lastZoomTime == 5 && event.deltaY > 0) || event.deltaY > 100){
          console.log('lastZoomTime:', lastZoomTime); // Unit of measurement
          this.viewportZoomControl.changeValue(this.viewportZoomControl.value - 10)        
        }
        if(Math.abs(event.deltaY)< 100){                
          if(lastZoomTime > 5){
            lastZoomTime = 0;``
            avgDeltaY = 0
          }    
          avgDeltaY +=event.deltaY
          lastZoomTime++;
          
        }
        
        event.preventDefault(); 
      }
    });
    viewPort.addEventListener("keydown", (event: KeyboardEvent) => {
      console.log(event.key);
      if (event.ctrlKey && (event.key === "=" || event.key === "+")) {
        this.viewportZoomControl.changeValue(this.viewportZoomControl.value + 10)
        event.preventDefault(); // Prevents browser zoom (optional)
      }
      if (event.ctrlKey && (event.key === "_" || event.key === "-")) {
        this.viewportZoomControl.changeValue(this.viewportZoomControl.value - 10)
        event.preventDefault(); // Prevents browser zoom (optional)
      }
    });
    
    this.addRCExpIncreamentor();
    // Add default tools and layers for demo
    this.initialize(options);
    this.changeZoom();

    this.eventEmitter = EventEmitter.getInstance();
    (window as any).lb = this;
  }
  private async addRCExpIncreamentor(){
    this.screenSizeControl = this.createDropdown("screeSizeSelector", this.options.screenSizes)
    const rcExpIncContainer = document.querySelector('#layoutGridLineMultiplier');    
    if(rcExpIncContainer){
      rcExpIncContainer.innerHTML = "";
      rcExpIncContainer.appendChild(this.rcExpIncreamentor.container);    
      rcExpIncContainer.appendChild(this.viewportZoomControl.container);  
      rcExpIncContainer.appendChild(this.screenSizeControl);
      this.screenSizeControl.addEventListener("change", ()=>this.changeScreenSize());
     }
   
  }
  private createStructure(): void {
    this.host.innerHTML = `
      <div id="${CSS_CLASSES.ID.LAYOUT_BUILDER}">
        <div id="${CSS_CLASSES.ID.TOOLBOX}">
          <div class='${CSS_CLASSES.TOOLBOX_PANEL}'></div>
        </div>
        <div id="${CSS_CLASSES.ID.VIEWPORT}"  tabindex= -1>
          <div id="${CSS_CLASSES.ID.SCREENSIZE_ADAPTOR}" style="width:100%;height100%;box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.8"  tabindex= -1>
            <div id='${CSS_CLASSES.ID.VIEWPORT_CONTAINER}' tabindex= -1>
              <div class='${CSS_CLASSES.DRAGGABLE_CONTAINER}' tabindex= -1></div>
              <div id="canvas" class="canvas"></div>
            </div>
          </div>
        </div>
        <div id="${CSS_CLASSES.ID.LAYER_PROPERTY_CONTAINER}" style="z-index:1">
          <div id="layoutGridLineMultiplier" style="width:100%">
          </div>
          <div id="${CSS_CLASSES.ID.PROPERTY_CONTAINER}">
            <h3>Properties</h3>
            <div class='${CSS_CLASSES.PROPERTY_PANEL}'></div>
          </div>
        </div>
      </div>
    `;
  }
 
  private getExponentToBringUnder100(val:number):number{
      if(val<=100)
        return 0
      else
        return (this.getExponentToBringUnder100(val/2) + 1)
    }
  public getPreviewScreenSizeControl(){
    const rcExpIncContainer = document.querySelector('#layoutGridLineMultiplier'); 
    const screenSizeSelector = rcExpIncContainer?.querySelector("#screeSizeSelector")   
    if(screenSizeSelector){
      rcExpIncContainer?.removeChild(screenSizeSelector)
      return screenSizeSelector;
    }
    return null;
  }
  private createDropdown(id: string, items:{ label: string; width: number; height: number }[]): HTMLSelectElement {
    const input = document.createElement("select");
    input.id = id;
    items.forEach(item => {
      const option = document.createElement("option");
      option.textContent = item.label;
      option.value = JSON.stringify(item);
      input.options.add(option);
    });
    input.classList.add("slider");
    return input;
  }
 
  private initialize(options: ILayoutBuilderOptions): void {
    // Add tools to the toolbox
    this.toolboxManager.addDefaultTools();
    if (options.layoutConfig) {
      this.initializeFromConfig(options.layoutConfig);
      return;
    }
    const viewport  = this.host.querySelector('#viewport-container')!;
   
    const viewportWidth = viewport.getBoundingClientRect().width;
    const viewportHeight = viewport.getBoundingClientRect().height;
    const cellWidth = Math.floor(Math.max(viewportWidth, viewportHeight) / this.stateManager.getState().colCount);
    const cellHeight = Math.floor(Math.max(viewportWidth, viewportHeight) / this.stateManager.getState().rowCount);
    let state = this.stateManager.getState();
    state.cellHeight = cellWidth;
    state.cellWidth = cellHeight;
    this.stateManager.setState({state:state,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
    this.layoutManager.addLayer(Math.pow(2,this.rcExpIncValue));
    //adding this because even if we give a particular width to td is not render width so need to calculate again
    //these will be the exact width/height of the cell
    state.cellHeight = (this.ruler?.pixelsPerMMY)?this.ruler?.pixelsPerMMY : state.cellHeight;
    state.cellWidth = (this.ruler?.pixelsPerMMX)?this.ruler?.pixelsPerMMX : state.cellWidth;
    this.stateManager.setState({state:state,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
  }
 
  private refreshLayout(config: LayoutConfig){
    this.stateManager.setState({
      state:config,
      changeType:changeType.UPDATED_VIEWPORT_CONFIG,
    });
    const state = config;
    this.layoutManager.addLayer(Math.pow(2,this.rcExpIncValue));
   
    state.cellHeight = (this.ruler?.pixelsPerMMY)?this.ruler?.pixelsPerMMY : state.cellHeight;
    state.cellWidth = (this.ruler?.pixelsPerMMX)?this.ruler?.pixelsPerMMX : state.cellWidth;
   
    this.stateManager.setState({state:state,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
    this.refresh();
  }
 
  private initializeFromConfig(config: LayoutConfig){
    // Implementation for initializing from config
    if(!config.defaultScreenSize){
      config.defaultScreenSize = this.options.screenSizes[0];
    }
    this.stateManager.setState({
      state:config,
      changeType:changeType.UPDATED_VIEWPORT_CONFIG,
    });
    
    this.layoutManager.renderLayer({
      cols:config.colCount,
      rows:config.rowCount,
      containers:[],
      name:"viewport"
    })
    const state = config;
    if(state.defaultScreenSize)
      this.setScreeSize(state.defaultScreenSize,true);
    this.layoutManager.addLayer(Math.pow(2,this.rcExpIncValue));
   
    state.cellHeight = (this.ruler?.pixelsPerMMY)?this.ruler?.pixelsPerMMY : state.cellHeight
    state.cellWidth = (this.ruler?.pixelsPerMMX)?this.ruler?.pixelsPerMMX : state.cellWidth
   
    this.stateManager.setState({state:state,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
    this.refresh();

  }
 
 
    public toJSON(){
      return JSON.parse(JSON.stringify(this.stateManager.getState()))
    }
 
 
    public fromJSON(state:LayoutConfig){
      this.rcExpIncreamentor.setToDefault();
      this.viewportZoomControl.setToDefault();
      this.stateManager.resetState();
      this.initializeFromConfig(state);
    }
 
    public setPreviewMode(mode:boolean){
      this.host.classList.toggle('preview-mode',mode);
    }
    public isPreviewMode(){
      return this.host.classList.contains('preview-mode');
    }
    private changeGridDensity(){
      if(this.rcExpIncreamentor){
        const newVal = Number(this.rcExpIncreamentor.value)
        if(newVal != this.rcExpIncValue){
          let state = this.stateManager.getState();
          this.UpdateConfig(state, newVal - this.rcExpIncValue)
          this.rcExpIncValue = newVal;        
          this.refreshLayout(state);
        }
      }
    }
    public changeScreenSize(){
      if(this.screenSizeControl?.value && this.ruler){
        let selectedItem:IScreenSize = JSON.parse(this.screenSizeControl?.value)
        this.setScreeSize(selectedItem);
      }
    }
    private changeZoom(){
      if(this.viewportZoomControl){
        const newVal = Number(this.viewportZoomControl.value)
        this.ruler?.zoom(newVal/100);
        this.stateManager.setZoomValue(newVal/100);
        //this.dragdropManager.setZoomScale([newVal/100,newVal/100]);
        this.refresh();
      }
    }
   
    public setScreeSize(screeSize:IScreenSize, reset:boolean = false){
      const oldState = this.stateManager.getState();
      if(oldState.defaultScreenSize){
        let sizeInPixel = this.ruler?.getSizeInPixels({width:screeSize.width,height:screeSize.height},screeSize.unit);
        let oldSizeInPixel = this.ruler?.getSizeInPixels({width:oldState.defaultScreenSize.width,height:oldState.defaultScreenSize.height}, oldState.defaultScreenSize.unit);
        let screenSizeAdaptor = this.host.querySelector(`#${CSS_CLASSES.ID.SCREENSIZE_ADAPTOR}`) as HTMLDivElement;
        let scale:[number,number]=[1,1];
        //if(oldState.scale)
        //  scale = oldState.scale;
        if(sizeInPixel && oldSizeInPixel){
          scale = [sizeInPixel.width / (oldSizeInPixel.width * scale[0]), sizeInPixel.height / (oldSizeInPixel.height * scale[1])];
          oldState.scale = scale;
        }
        //oldState.defaultScreeSize = screeSize;
        this.stateManager.setState({state:oldState,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
        if(sizeInPixel?.width)
          oldState.width = sizeInPixel?.width * scale[0]
        if(sizeInPixel?.height)
          oldState.height = sizeInPixel?.height * scale[1]
        if(sizeInPixel){
          screenSizeAdaptor.style.width = `${Math.round(sizeInPixel.width)}px`;
          screenSizeAdaptor.style.height = `${Math.round(sizeInPixel.height)}px`;
          this.refresh();
        }
      }
    }
    private UpdateConfig(config:LayoutConfig, newVal:number){
      let multiplier = Math.pow(2,Math.abs(newVal))
      let divider = 1;
      if(newVal < 0){
        divider = multiplier;
        multiplier = 1;
      }
      
      config.rowCount = (config.rowCount * multiplier)/divider
      config.colCount = (config.colCount * multiplier)/divider
      config.sections.forEach(s => {
        s.col = Math.round((s.col * multiplier)/divider)
        s.row = Math.round((s.row * multiplier)/divider)
        s.rowCount = Math.round((s.rowCount * multiplier)/divider)
        s.colCount = Math.round((s.colCount * multiplier)/divider)
      });
    }
    public refresh(){
      this.ruler?.refresh();
      const zoomValue = this.stateManager.getZoomValue();
      const multiplier = this.stateManager.getMultipler();
      const oldState = this.stateManager.getState();
      const scale = (oldState.scale)?oldState.scale:[1,1];
      oldState.cellHeight = (this.ruler?.pixelsPerMMY)?this.ruler?.pixelsPerMMY:oldState.cellHeight;
      oldState.cellWidth = (this.ruler?.pixelsPerMMX)?this.ruler?.pixelsPerMMX:oldState.cellWidth;
      this.stateManager.setState({state:oldState, zoomValue : zoomValue, multiplier: multiplier,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
      const viewport = this.host.querySelector(`#${CSS_CLASSES.ID.SCREENSIZE_ADAPTOR}`) as HTMLDivElement;
      viewport.style.width = `${oldState.colCount * (oldState.cellWidth / multiplier) * scale[0]  * zoomValue}px`;
      viewport.style.height = `${oldState.rowCount * (oldState.cellHeight / multiplier) * scale[1] * zoomValue}px`;        
      this.layoutManager.refreshLayers();
      LayoutManager.syncStateToDOM({state:oldState, zoomValue : zoomValue, multiplier: multiplier,changeType:changeType.UPDATED_VIEWPORT_CONFIG});
    }
    public destroy(){
      this.propertyManager.destroy();
      this.toolboxManager.destroy();
      this.layoutManager.destroy();
      this.ruler?.destroy();
      this.host.innerHTML = '';
    }

    public bind(eventName:string, callback:EventCallback){

      if(!(EVENTS as any)[eventName.toUpperCase()]){
        throw new Error("Not a valid EventName");
      } 
      return {
         unsubscribe: this.eventEmitter.subscribe(eventName.toUpperCase(),callback)
    }
  }
}
 
 
 